import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import type { RoleCardData } from "./useRolesSubPage";

export interface RoleCardProps {
  role: RoleCardData;
  /** Omitted for default roles, which have no stored record and are rendered read-only. */
  onSelect?: (roleId: string) => void;
}

export const RoleCard = ({ role, onSelect }: RoleCardProps) => {
  const { t } = useTranslation("settings");

  const interactive = !!onSelect && !role.isDefault;

  return (
    <Card
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? () => onSelect(role.id) : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelect(role.id);
              }
            }
          : undefined
      }
      data-trackingid="roles-subpage-role-card"
      className={
        interactive
          ? "border shadow-none p-4 cursor-pointer transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring gap-2"
          : "border shadow-none p-4 gap-2"
      }
    >
      <CardHeader className="p-0">
        <CardTitle className="text-sm flex gap-2 items-center">
          <div
            style={{ backgroundColor: role.color ?? "#ffffff" }}
            className="size-3 rounded-full border border-gray-500/30"
          />
          {role.role}
          {role.isDefault && (
            <Badge variant="secondary" className="ml-auto">
              {t("Default")}
            </Badge>
          )}
        </CardTitle>
        {!!role.description && <CardDescription>{role.description}</CardDescription>}
      </CardHeader>
      <CardContent className="text-xs text-muted-foreground p-0 space-y-1">
        <p>{t("Users: {{amount}}", { amount: role.userCount })}</p>
        <p>{t("Permissions: {{amount}}", { amount: role.permissionCount })}</p>
      </CardContent>
    </Card>
  );
};
